<template>
  <div class="tab-container">
    <app-cells>
      <VueCtkDateTimePicker
        id="date_picker_sup_statement"
        ref="date_picker"
        v-model="filter_dates"
        :custom-shortcuts="custom_shortcuts"
        shortcut="thisYear"
        button-color="#77b017"
        class="custom-date-time-picker custom-date-time-picker--range"
        color="#77b017"
        format="YYYY-MM-DD"
        formatted="ll"
        label="Date range"
        locale="en"
        no-header
        no-label
        overlay
        range
        @input="onInputDatePicker"
        @is-hidden="onHiddenPicker"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row"><h3 class="title">TZS</h3></div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>EFD Purchases:</span><span>{{ amountFormat(stats.efd_purchases_tzs) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>EFD Sales:</span><span>{{ amountFormat(stats.efd_sales_tzs) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Cashflow Outcome:</span><span>{{ amountFormat(stats.cashflow_outcome_tzs) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Cashflow Income:</span><span>{{ amountFormat(stats.cashflow_income_tzs) }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row"><h3 class="title">USD</h3></div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>EFD Purchases:</span><span>{{ amountFormat(stats.efd_purchases_usd) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>EFD Sales:</span><span>{{ amountFormat(stats.efd_sales_usd) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Cashflow Outcome:</span><span>{{ amountFormat(stats.cashflow_outcome_usd) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Cashflow Income:</span><span>{{ amountFormat(stats.cashflow_income_usd) }}</span>
            </div>
          </div>
        </div>
      </div>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default table-default--maxw"
      >
        <template #h__type><div class="table-default__left">Type</div></template>
        <template #h__reference><div class="table-default__left">Reference</div></template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #type="{ row }"><div class="table-default__left">
          <span v-if="row.type === 'cashflow_outcome'" class="table-default__text-red">Outcome</span>
          <span v-if="row.type === 'cashflow_income'" class="table-default__text-green">Income</span>
          <span v-if="row.type === 'efd_output'" class="table-default__text-red">EFD Output</span>
          <span v-if="row.type === 'efd_input'" class="table-default__text-green">EFD Input</span>
        </div></template>
        <template #reference="{ row }"><div class="table-default__left">{{ row.reference }}</div></template>
        <template #amount="{ row }">{{ amountFormat(row.amount) }} {{ row.currency }}</template>
        <template #amount_usd="{ row }">{{ amountFormat(row.amount_usd) }} $</template>
        <template #amount_tzs="{ row }">{{ amountFormat(row.amount_tzs) }} TZS</template>
      </v-client-table>
    </template>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { getAdministrativeSuppliersStatement, getAdministrativeSupplierFullStats } from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'SuppliersDetailStatement',
  components: {VueCtkDateTimePicker},
  data() {
    return {
      page_loaded: false,
      filter_dates: null,
      custom_shortcuts: [
        {key: 'last7Days', label: 'Last 7 days', value: 7},
        {key: 'last30Days', label: 'Last 30 days', value: 30},
        {key: 'last60Days', label: 'Last 60 days', value: 60},
        {key: 'last90Days', label: 'Last 90 days', value: 90},
        {key: 'last180Days', label: 'Last 180 days', value: 180},
        {key: 'thisYear', label: 'This year', value: 'year'},
        {key: 'lastYear', label: 'Last year', value: '-year'},
      ],
      table: {
        items: [],
        columns: [
          'date',
          'type',
          'reference',
          'amount',
          'amount_usd',
          'amount_tzs',
        ],
        options: {
          headings: {
            'date': 'Date',
            'type': 'Type',
            'reference': 'Reference',
            'amount': 'Amount',
            'amount_usd': 'Amount USD',
            'amount_tzs': 'Amount TZS',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 500,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      stats: {},
    }
  },
  methods: {
    async fetchItems() {
      const response = await getAdministrativeSuppliersStatement(this.$route.params.id, {
        from_date: this.filter_dates?.start,
        to_date: this.filter_dates?.end,
      })
      this.table.items = response.data.records
      this.page_loaded = true
    },
    async fetchStats() {
      const response = await getAdministrativeSupplierFullStats(this.$route.params.id, {
        from_date: this.filter_dates?.start,
        to_date: this.filter_dates?.end,
      })
      this.stats = response.data
    },
    onInputDatePicker() {
      if (!this.$refs.date_picker.pickerOpen) {
        this.fetchItems()
        this.fetchStats()
      }
    },
    onHiddenPicker() {
      if (this.filter_dates?.start) {
        this.fetchItems()
        this.fetchStats()
      }
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return new Intl.NumberFormat('ru-RU').format(amount)
    },
  }
}
</script>

